import { createContext, useContext, useMemo, useState, type CSSProperties, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import classNames from 'classnames';
import { toTourTargetProps, type TourTarget } from '@sorrem-client/ui/shared/tour-targets';
import { useElementSize } from './use-element-size';
import styles from './HudAreas.module.scss';

/** A corner area of the HUD. */
type HudArea = 'top-left' | 'bottom-left' | 'top-right' | 'bottom-right';

/** A HUD panel placed in a corner area with `HudAreaSlot`. */
export type HudAreaPanel = 'carried' | 'party' | 'map' | 'commission';

const HUD_AREAS: readonly HudArea[] = ['top-left', 'bottom-left', 'top-right', 'bottom-right'];

/** The corner each panel sits in. */
const PANEL_AREAS: Record<HudAreaPanel, HudArea> = {
    carried: 'top-left',
    party: 'bottom-left',
    map: 'top-right',
    commission: 'bottom-right',
};

/** Each area's guided-tour target (#234), named for the panel it holds, so a tour step can point at the panel wherever it is. */
const AREA_TOUR_TARGETS = Object.fromEntries(
    (Object.entries(PANEL_AREAS) as [HudAreaPanel, HudArea][]).map(([panel, area]): [HudArea, TourTarget] => [area, `hud-${panel}`]),
) as Record<HudArea, TourTarget>;

/** Gap kept between the open Carried drawer and the party area under it. */
const HUD_AREA_CLEARANCE = 24;
/** Each HUD area's class in HudAreas.module.scss. */
const HUD_AREA_CLASS_NAMES: Record<HudArea, string> = {
    'top-left': 'areaTopLeft',
    'bottom-left': 'areaBottomLeft',
    'top-right': 'areaTopRight',
    'bottom-right': 'areaBottomRight',
};

/** The corner area containers on screen, once rendered. */
const HudAreasContext = createContext<Partial<Record<HudArea, HTMLElement>> | null>(null);

interface HudAreaFrameProps {
    /** What heads the top-right area, above its panel (the run's settings gear). */
    readonly topRightHead?: ReactNode;
    /** The screen's panels, placed with `HudAreaSlot`, and anything else that zooms with the HUD. */
    readonly children: ReactNode;
}

/**
 * The HUD's four corner areas, shared by the run's HUD and the gathering, so
 * each panel stands in the same place on both. The bottom areas sit on the
 * screen's bottom edge, beside the centered loadout. Sets --hud-scale for
 * everything inside it: the areas zoom by it, and so should any other HUD piece.
 */
export function HudAreaFrame({ topRightHead, children }: HudAreaFrameProps): JSX.Element {
    const [elements, setElements] = useState<Partial<Record<HudArea, HTMLElement>>>({});
    // Stable refs: a new one each render would detach and reattach every area.
    const areaRefs = useMemo(() => Object.fromEntries(HUD_AREAS.map((area) => [area, (element: HTMLDivElement | null): void => {
        setElements((current) => (current[area] === (element ?? undefined) ? current : { ...current, [area]: element ?? undefined }));
    }])) as Record<HudArea, (element: HTMLDivElement | null) => void>, []);
    // The open Carried drawer keeps clear of the party area under it.
    const partyAreaHeight = useElementSize(elements['bottom-left'] ?? null)?.height ?? 0;
    const carriedAreaStyle = { '--carried-panel-reserved-height': `${partyAreaHeight + HUD_AREA_CLEARANCE}px` } as CSSProperties;

    return (
        <div className={styles.hudFrame}>
            <HudAreasContext.Provider value={elements}>
                {HUD_AREAS.map((area) => (
                    <div
                        key={area}
                        ref={areaRefs[area]}
                        className={classNames(styles.hudArea, styles[HUD_AREA_CLASS_NAMES[area]])}
                        style={area === 'top-left' ? carriedAreaStyle : undefined}
                        {...toTourTargetProps(AREA_TOUR_TARGETS[area])}
                    >
                        {area === 'top-right' && topRightHead}
                    </div>
                ))}
                {children}
            </HudAreasContext.Provider>
        </div>
    );
}

interface HudAreaSlotProps {
    readonly panel: HudAreaPanel;
    /** The panel, rendered straight into its corner area: no wrapper of its own. */
    readonly children: ReactNode;
}

/** Renders a HUD panel into its corner area. */
export function HudAreaSlot({ panel, children }: HudAreaSlotProps): JSX.Element | null {
    const element = useContext(HudAreasContext)?.[PANEL_AREAS[panel]];
    return element === undefined ? null : createPortal(children, element);
}
